import { Component, EventEmitter, Output, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, combineLatest } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { LanguageService } from '../../../services/language.service';

@Component({
  selector: 'app-forgot-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './forgot-password-modal.html',
  styleUrl: './forgot-password-modal.scss'
})
export class ForgotPasswordModalComponent implements OnInit, OnDestroy {
  @Output() closeModal = new EventEmitter<void>();

  verifyForm: FormGroup;
  resetForm: FormGroup;
  isLoading = false;
  stage: 'verify' | 'reset' = 'verify';
  private subscriptions: Subscription[] = [];
  private verifiedUserId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toastService: ToastService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {
    this.verifyForm = this.fb.group({
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      email: ['', [Validators.required, Validators.email]],
      dob: ['', [Validators.required]]
    });

    this.resetForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([
        this.languageService.currentLanguage$,
        this.languageService.translations$
      ]).subscribe(() => {
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  t(key: string, params?: { [key: string]: string | number }): string {
    return this.languageService.t(key, params);
  }

  onOverlayClick(): void {
    this.onCloseModal();
  }

  onCloseModal(): void {
    this.closeModal.emit();
  }

  onVerifySubmit(): void {
    if (this.verifyForm.invalid) {
      this.verifyForm.markAllAsTouched();
      return;
    }

    const { mobile, email, dob } = this.verifyForm.value;
    this.isLoading = true;

    this.authService.verifyUserForPasswordReset({ mobile, email, dob }).subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result.success && result.userId) {
          this.verifiedUserId = result.userId;
          this.stage = 'reset';
          this.toastService.success(this.t('forgotPasswordDetailsVerified') || 'Details verified. Please set a new password.');
        } else {
          this.toastService.error(result.message || this.t('forgotPasswordNoMatch') || 'No matching user found with provided details.');
        }
      },
      error: () => {
        this.isLoading = false;
        this.toastService.error(this.t('forgotPasswordVerifyFailed') || 'Failed to verify details. Please try again.');
      }
    });
  }

  onResetSubmit(): void {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    const { newPassword, confirmPassword } = this.resetForm.value;
    if (newPassword !== confirmPassword) {
      this.toastService.error('New password and confirm password do not match.');
      return;
    }

    if (!this.verifiedUserId) {
      this.toastService.error('Verification step is required before resetting password.');
      return;
    }

    this.isLoading = true;
    this.authService.resetPasswordForVerifiedUser(this.verifiedUserId, newPassword).subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result.success) {
          this.toastService.success(this.t('forgotPasswordResetSuccess') || 'Password reset successfully. You can now login with your new password.');
          this.onCloseModal();
        } else {
          this.toastService.error(result.message || this.t('forgotPasswordResetFailed') || 'Failed to reset password. Please try again.');
        }
      },
      error: () => {
        this.isLoading = false;
        this.toastService.error(this.t('forgotPasswordResetFailed') || 'Failed to reset password. Please try again.');
      }
    });
  }

  get mobileControl() {
    return this.verifyForm.get('mobile');
  }

  get emailControl() {
    return this.verifyForm.get('email');
  }

  get dobControl() {
    return this.verifyForm.get('dob');
  }

  get newPasswordControl() {
    return this.resetForm.get('newPassword');
  }

  get confirmPasswordControl() {
    return this.resetForm.get('confirmPassword');
  }
}

